import apiClient from '../lib/apiClient';

// Define the User interface based on expected data from getMe
export interface User {
  id: string;
  email: string;
  name: string | null;
  phone?: string | null; // Optional: Add if your backend can provide it
  profileImageUrl?: string | null; // Optional: Add if your backend can provide it
  customData?: {
    admin?: boolean;
  };
  // Impersonation info
  isImpersonating?: boolean;
  originalAdminId?: string;
  // Removed [key: string]: any; for stricter typing. Add back if other dynamic properties are essential.
}

import { z } from 'zod';
const UserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string().nullable(),
  phone: z.string().nullable().optional(),
  profileImageUrl: z.string().url().nullable().optional(),
  admin: z.boolean()
});

const navigationMenuItemSchema = z.object({
  id: z.number(),
  translationKey: z.string(),
  href: z.string(),
  icon: z.string(),
  exact: z.boolean(),
  order: z.number(),
  active: z.boolean(),
  isSystem: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
})


export type NavigationMenuItem = z.infer<typeof navigationMenuItemSchema>;

const userNavigationMenuItemSchema = z.object({
  id: z.number(),
  userId: z.string(),
  menuId: z.number(),
  enabled: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type UserNavigationMenuItem = z.infer<typeof userNavigationMenuItemSchema>;

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

/**
 * Initiates the sign-in process by redirecting the user to the
 * external authentication provider's sign-in page.
 */
export const signIn = () => {
  if (!BASE_URL) {
    console.error('VITE_API_BASE_URL is not defined. Cannot redirect to sign-in.');
    // Optionally, throw an error or display a message to the user
    return;
  }
  window.location.href = `${BASE_URL}/auth/sign-in`;
};

/**
 * Fetches the current authenticated user's data.
 * Relies on the HttpOnly session cookie being present.
 * @returns A Promise that resolves to the User object.
 */
export async function getMe(): Promise<User> {
  // The existing getMe implementation uses apiClient which hits /users/validate-token.
  // This endpoint should be the one that returns user data based on the session cookie.
  return apiClient.get<User>('/auth/me') as unknown as User;
}

/**
 * Initiates the sign-out process by redirecting the user to the
 * backend sign-out endpoint, which should clear the session cookie
 * and potentially redirect to a home/login page.
 */
export const signOut = () => {
  if (!BASE_URL) {
    console.error('VITE_API_BASE_URL is not defined. Cannot redirect to sign-out.');
    // Optionally, throw an error or display a message to the user
    return;
  }
  // The previous signOut made an API call: await apiClient.post('/auth/sign-out');
  // The new requirement is to redirect, assuming the backend endpoint handles cookie clearing.
  window.location.href = `${BASE_URL}/auth/sign-out`;
};

/**
 * Updates the user's profile information.
 * @param userData A partial User object containing the fields to update.
 * @returns A Promise that resolves to the updated User object.
 */
export async function updateUserProfile(userData: Partial<User>): Promise<User> {
  return apiClient.post<User>('/users/profile', userData) as unknown as User;
}

//backoffice

export async function fetchUsers() {
  const data = await apiClient.get('/backoffice/users');

  const parsed = z.array(UserSchema).safeParse(data);

  if (!parsed.success) {
    throw new Error('Respuesta inválida al obtener períodos de facturación');
  }

  return parsed.data;
}

export async function getNavigateMenu() {
  const data = await apiClient.get('/backoffice/navigation-menu');

  const parsed = z.array(navigationMenuItemSchema).safeParse(data);

  if (!parsed.success) {
    throw new Error('Respuesta inválida ');
  }

  return parsed.data;
}

export async function updateNavigateMenu(item: NavigationMenuItem) {
  const data = await apiClient.patch(`/backoffice/navigation-menu/${item.id}`, item);

  return data;
}

export async function getUserNavigateMenus(userId: string) {
  const data = await apiClient.get(`/backoffice/user-navigation-menu/user/${userId}`);

  const parsed = z.array(userNavigationMenuItemSchema).safeParse(data);

  if (!parsed.success) {
    throw new Error('Respuesta inválida ');
  }

  return parsed.data;
}

export async function createUserNavigateMenu(item: {userId: string, enabled: boolean, menuId: number}) {
  const data = await apiClient.post(`/backoffice/user-navigation-menu/permission`, item);

  return data
}

/**
 * Impersonate a user (Admin only)
 * Allows an admin to login as another user for support purposes
 * @param targetUserId The ID of the user to impersonate
 * @returns A Promise that resolves when impersonation is successful
 */
export async function impersonateUser(targetUserId: string): Promise<void> {
  await apiClient.post('/backoffice/users/impersonate', { targetUserId });
  
  // Redirect to home page after successful impersonation
  window.location.href = '/';
}

/**
 * Stop impersonating and return to admin session
 * @returns A Promise that resolves when impersonation is stopped
 */
export async function stopImpersonation(): Promise<void> {
  const response = await apiClient.post<{ success: boolean; restored?: boolean; requiresReauth?: boolean }>('/backoffice/users/stop-impersonation', {});
  
  if (response.restored) {
    // Admin session successfully restored from Redis
    // Redirect to home to refresh with restored admin session
    window.location.href = '/';
  } else {
    // Session restoration failed or expired
    // Redirect to the gateway's sign-in endpoint for re-authentication
    window.location.href = `${import.meta.env.VITE_API_BASE_URL}/auth/sign-in`;
  }
}

export async function deleteUserNavigateMenu(item: {userId: string, menuId: number}) {
  const data = await apiClient.delete(`/backoffice/user-navigation-menu/permission/${item.userId}/${item.menuId}`);
  return data
}

export async function changeUserRol(userId:string, admin:boolean) {
  console.log(userId);
  
  const data = await apiClient.patch(`/backoffice/users/${userId}/admin`, {admin});
  console.log(data);
  
  return data;
}